import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useMembershipPlans } from '@/hooks/useMembershipPlans';
import { Badge, Button, Card, CardTitle } from '@/components/ui';
import { formatCOP } from '@/lib/format';

type Cycle = 'monthly' | 'annual';

/**
 * MembershipTier — ROUND7 P2 / ROUND8 UAT-C04 / ROUND12. Detalle de un plan.
 *
 * ROUND12 (admin R26): selector Mensual ↔ Anual con UX clara:
 *  - Default Mensual (overridable vía `?cycle=annual`, usado por el deep-link
 *    "Renovar ahora" desde /account/membership).
 *  - Anual = 10 meses por el precio de 12 → ahorrás 2 meses (~16.7%).
 *  - Precio + puntos refrescan según el ciclo seleccionado.
 *  - CTA navega a `/checkout?mode=membership&tier=<slug>&cycle=<cycle>` y el
 *    `Checkout` invoca `fn_activate_subscription` tras el pago aprobado.
 */
export function MembershipTierPage(): JSX.Element {
  const { tierSlug } = useParams<{ tierSlug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: plans, isLoading, isError } = useMembershipPlans();

  const cycleParam = searchParams.get('cycle');
  const defaultCycle: Cycle = cycleParam === 'annual' ? 'annual' : 'monthly';
  const [cycle, setCycle] = useState<Cycle>(defaultCycle);

  if (isLoading) {
    return (
      <section className="container-fc py-12" aria-busy="true">
        <div className="h-8 w-48 rounded bg-neutral-100" />
        <div className="mt-4 h-5 w-full max-w-md rounded bg-neutral-100" />
        <div className="mt-8 h-40 w-full rounded-card bg-neutral-100" />
      </section>
    );
  }

  const tier = plans?.find((p) => p.slug === tierSlug) ?? null;

  if (isError || !tier) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">
          {isError ? 'No pudimos cargar el plan' : 'Plan no encontrado'}
        </h1>
        <p className="mt-2 text-fc-muted">
          {isError
            ? 'Ocurrió un error al cargar este plan. Probá recargar la página en unos minutos.'
            : 'El plan que buscás no existe. Mirá los planes disponibles.'}
        </p>
        <Button className="mt-6" variant="secondary" onClick={() => navigate('/memberships')}>
          Ver todas las membresías
        </Button>
      </section>
    );
  }

  const isFree = tier.monthlyPriceCents === 0;
  const hasAnnual =
    typeof tier.annualPriceCents === 'number' && tier.annualPriceCents > 0 && !isFree;
  const effectiveCycle: Cycle = hasAnnual ? cycle : 'monthly';

  const monthly = tier.monthlyPriceCents ?? 0;
  const annual = tier.annualPriceCents ?? 0;
  const displayPriceCop = effectiveCycle === 'annual' && hasAnnual ? annual : monthly;
  const points = effectiveCycle === 'annual' ? tier.pointsAnnual : tier.pointsMonthly;
  const annualPerMonth = hasAnnual ? Math.round(annual / 12) : null;

  function handleSubscribe(): void {
    if (!tier) return;
    navigate(`/checkout?mode=membership&tier=${tier.slug}&cycle=${effectiveCycle}`);
  }

  const ctaLabel = isFree
    ? 'Empezar gratis'
    : effectiveCycle === 'annual'
      ? 'Suscribirme anual'
      : 'Suscribirme mensual';

  return (
    <section className="container-fc py-8 md:py-12">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-fc-muted">
        <Link to="/memberships" className="hover:text-fc-ink">
          Membresías
        </Link>
        <span aria-hidden> / </span>
        <span className="text-fc-ink">{tier.name}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        <header className="md:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={tier.tone}>{tier.name}</Badge>
            {tier.recommended && <Badge tone="gold">Recomendado</Badge>}
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl">{tier.tagline}</h1>
          <p className="mt-3 text-sm text-fc-muted">{tier.targetAudience}</p>
        </header>

        <Card variant="outlined" padded className="md:col-span-1">
          {/* ROUND12 — selector de ciclo. Visible solo cuando hay precio anual. */}
          {hasAnnual && (
            <div className="mb-4">
              <div
                role="tablist"
                aria-label="Ciclo de facturación"
                className="grid grid-cols-2 gap-1 rounded-card border border-fc-line bg-fc-bg/40 p-1"
              >
                <CycleTab active={effectiveCycle === 'monthly'} onClick={() => setCycle('monthly')}>
                  Mensual
                </CycleTab>
                <CycleTab active={effectiveCycle === 'annual'} onClick={() => setCycle('annual')}>
                  Anual
                </CycleTab>
              </div>
              {effectiveCycle === 'annual' && (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-medium text-success">
                  Ahorrás 2 meses · 10 meses por el precio de 12
                </p>
              )}
            </div>
          )}

          <div className="text-xs uppercase tracking-wide text-fc-muted">Precio</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums">
            {tier.monthlyPriceCents === null
              ? 'A medida'
              : isFree
                ? 'Gratis'
                : formatCOP(displayPriceCop)}
          </div>
          {!isFree && tier.monthlyPriceCents !== null && (
            <div className="text-xs text-fc-muted">
              {effectiveCycle === 'annual' ? '/ año COP' : '/ mes COP'}
            </div>
          )}
          {hasAnnual && effectiveCycle === 'annual' && annualPerMonth !== null && (
            <div className="mt-2 text-sm text-fc-muted">≈ {formatCOP(annualPerMonth)} / mes</div>
          )}
          {hasAnnual && effectiveCycle === 'monthly' && (
            <div className="mt-2 text-sm text-fc-muted">
              o {formatCOP(annual)} al año <span className="text-success">(ahorrás 2 meses)</span>
            </div>
          )}
          {points > 0 && (
            <div className="mt-3 text-sm text-fc-muted">
              <span className="font-medium text-fc-ink">
                +{points.toLocaleString('es-CO')} puntos
              </span>{' '}
              al suscribirte
            </div>
          )}
          <Button
            className="mt-4 w-full justify-center"
            variant="primary"
            size="lg"
            onClick={handleSubscribe}
          >
            {ctaLabel}
          </Button>
          <Button
            as={Link}
            to="/memberships"
            variant="ghost"
            size="sm"
            className="mt-2 w-full justify-center"
          >
            Comparar otros planes
          </Button>
        </Card>
      </div>

      {tier.features.length > 0 && (
        <Card variant="default" padded className="mt-8">
          <CardTitle>Lo que incluye</CardTitle>
          <ul className="mt-4 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {tier.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-primary"
                />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {tier.benefits.length > 0 && (
        <Card variant="ghost" padded className="mt-6">
          <CardTitle>Detalle de beneficios</CardTitle>
          <ul className="mt-4 flex flex-col gap-3 text-sm text-fc-muted">
            {tier.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-accent"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

function CycleTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'rounded-card px-3 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        active
          ? 'bg-primary text-fc-bg shadow-sm'
          : 'text-fc-muted hover:bg-neutral-100 hover:text-fc-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
